import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSAutocomplete } from '../../../shared/components/form-controls';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { getApprovedSops, savePersonSop, deletePersonSop } from '../../services/sopService';

const AddressSopDialog = ({
  open,
  onClose,
  ptsAddressID,
  sopLink = null,
  existingSopIds = [],
  onSaved,
}) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.userData.user);
  const [submitting, setSubmitting] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [sopOptions, setSopOptions] = useState([]);
  const isEditMode = Boolean(sopLink?.ptsSOPID);

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: { ptsSOPID: sopLink?.ptsSOPID || null },
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  useEffect(() => {
    executeApiAction({
      setLoading: setLoadingOptions,
      dispatch,
      action: () => getApprovedSops(),
      onSuccess: (data) => setSopOptions(data),
    });
  }, [dispatch]);

  const ptsSOPID = useWatch({ control: methods.control, name: 'ptsSOPID' });

  const onSubmit = useCallback(
    async (data) => {
      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: async () => {
          if (isEditMode && !existingSopIds.includes(data.ptsSOPID)) {
            await deletePersonSop({
              ptsParentID: ptsAddressID,
              ParentType: 'Address',
              ptsSOPID: sopLink.ptsSOPID,
            });
          }

          return savePersonSop({
            ptsParentID: ptsAddressID,
            ParentType: 'Address',
            ptsSOPID: data.ptsSOPID,
            CreatedBy: currentUser?.Username,
            UpdatedBy: currentUser?.Username,
          });
        },
        onSuccess: () => {
          dispatch(
            notify(isEditMode ? 'SOP updated successfully' : 'SOP added successfully', 'success')
          );
          onSaved?.();
        },
      });
    },
    [isEditMode, existingSopIds, sopLink, ptsAddressID, currentUser, dispatch, onSaved]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this SOP. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  return (
    <>
      <RMSCommonDialog
        open={open}
        title={isEditMode ? 'Edit SOP' : 'Add SOP'}
        onClose={requestClose}
        onSave={handleSave}
        loading={submitting}
        saveDisabled={submitting || !ptsSOPID}
        maxWidth="sm"
        fullWidth
        closeOnBackdropClick={false}
        saveButton={{
          show: true,
          type: 'simple',
          label: 'Save',
          variant: 'contained',
          color: 'primary',
        }}
        cancelButton={{
          show: true,
          type: 'simple',
          label: 'Close',
          variant: 'outlined',
          color: 'secondary',
        }}
        leftActions={
          isEditMode && (
            <RMSRecordHistoryButton
              disabled={!sopLink?.Created}
              created={sopLink?.Created}
              createdBy={sopLink?.CreatedBy}
              updated={sopLink?.Updated}
              updatedBy={sopLink?.UpdatedBy}
            />
          )
        }>
        <FormProvider {...methods}>
          <RMSAutocomplete
            name="ptsSOPID"
            label="Search SOPs"
            options={sopOptions}
            optionValue="ptsSOPID"
            optionLabel="SOPID"
            loading={loadingOptions}
          />
        </FormProvider>
      </RMSCommonDialog>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </>
  );
};

export default AddressSopDialog;
